<script setup lang="ts">
import { computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { useAuthStore } from './stores/auth';
import { login, logout } from './lib/auth/auth0';
import { routes } from './router';
import type { AppConfig } from './lib/config';

const config = inject<AppConfig>('appConfig')!;
const authOn = config.features['auth'] === true;
const auth0 = authOn ? useAuth0() : null;
const auth = useAuthStore();
const route = useRoute();

// Pages that take a parameter (one experience) are reached from their list, not the nav.
const nav = routes.filter((r) => r.meta?.title !== undefined && !r.path.includes(':'));

const signedIn = computed(() => !authOn || auth.isAuthenticated);
const checking = computed(() => authOn && auth.isLoading);

function signIn(): void {
  if (auth0 !== null) {
    void login(auth0, config);
  }
}

function signOut(): void {
  if (auth0 !== null) {
    void logout(auth0);
  }
}
</script>

<template>
  <div v-if="checking || route.name === 'callback'" class="gate">
    <img src="/roxxy-logo-on-dark.png" alt="Roxxy" class="gate-logo" />
    <p class="muted">Signing you in…</p>
  </div>

  <div v-else-if="!signedIn" class="gate">
    <img src="/roxxy-logo-on-dark.png" alt="Roxxy" class="gate-logo" />
    <h1>Admin console</h1>
    <p class="muted">
      Manage knowledge, collateral, guided experiences, roles, prompts and connectors.
    </p>
    <p v-if="auth.lastError" class="notice notice-error">{{ auth.lastError }}</p>
    <button class="btn btn-primary" @click="signIn">Sign in</button>
  </div>

  <div v-else class="shell">
    <aside class="side">
      <div class="brand">
        <img src="/roxxy-logo-on-dark.png" alt="Roxxy" />
        <span class="rx-label">Admin</span>
      </div>
      <nav>
        <RouterLink v-for="r in nav" :key="String(r.name)" :to="r.path">
          {{ r.meta?.title }}
        </RouterLink>
      </nav>
      <div class="me">
        <span class="faint">{{ auth.displayName ?? (authOn ? '' : 'Auth off') }}</span>
        <button v-if="authOn" class="btn btn-ghost btn-sm" @click="signOut">Sign out</button>
      </div>
    </aside>
    <main class="main">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.gate {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  padding: 24px;
}
.gate-logo {
  height: 48px;
}

.shell {
  display: grid;
  grid-template-columns: 232px 1fr;
  min-height: 100vh;
}

.side {
  background: var(--rx-bg-sunken);
  border-right: 1px solid var(--rx-border);
  display: flex;
  flex-direction: column;
  padding: 20px 12px;
  gap: 24px;
  position: sticky;
  top: 0;
  height: 100vh;
  box-sizing: border-box;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px;
}
.brand img {
  height: 26px;
}

nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
nav a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  border-radius: var(--rx-radius-sm);
  color: var(--rx-text-muted);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
}
nav a:hover {
  color: var(--rx-text);
  background: var(--rx-surface);
}
nav a.router-link-active:not([href='/']),
nav a.router-link-exact-active {
  color: var(--rx-text);
  background: var(--rx-blue-wash);
}

.me {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 8px;
  overflow: hidden;
}
.me span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.main {
  min-width: 0;
}
</style>
